import { ChevronRightIcon, MicrophoneIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';

import type { TechnicalIssue } from '../../../pages/housekeeping/types/housekeeping';
import Text from '../../components/Text';
import { anciennete, libelleStatutPanne, lieuPanne } from '../../helpers/maintenance';
import { t } from '../../i18n/langue';

/**
 * Une panne de la liste : la vignette, le lieu, la catégorie et l'ancienneté. Une
 * urgence se voit avant d'être lue (pastille rouge), un message vocal aussi.
 */
interface CartePanneProps {
    panne: TechnicalIssue;
    onOuvrir: () => void;
    /** Le statut n'apprend rien dans « À prendre » (tout y est « signalé »). */
    avecStatut?: boolean;
    /** La catégorie dans la langue de l'app, traduite une fois pour toute la liste. */
    categorie?: string | null;
}

export default function CartePanne({ panne, onOuvrir, avecStatut = false, categorie }: CartePanneProps) {
    const urgent = panne.priority === 'critical';
    // Un vocal dont le fichier a disparu mais dont le texte reste compte aussi.
    const vocal = panne.has_voice_message ?? panne.has_audio;
    const details = [categorie ?? panne.category_name, anciennete(panne.created_at)].filter(Boolean).join(' · ');

    return (
        // mobile-check-ignore — la carte EST le contrôle, comme la carte de chambre.
        <button
            type="button"
            onClick={onOuvrir}
            className={`flex min-h-touch w-full items-center gap-3 rounded-xl bg-white px-3 py-3 text-left ${
                urgent ? 'border-2 border-alert-text-color' : ''
            }`}
        >
            {panne.photo_preview_url ? (
                <img
                    src={panne.photo_preview_url}
                    alt=""
                    loading="lazy"
                    className="h-16 w-16 shrink-0 rounded-lg bg-main-bg-color object-cover"
                />
            ) : (
                <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-main-color-light">
                    <WrenchScrewdriverIcon aria-hidden="true" className="h-7 w-7 text-main-color" />
                </span>
            )}
            <span className="min-w-0 flex-1">
                <Text variante="sousTitre" as="span" className="block truncate">
                    {lieuPanne(panne)}
                </Text>
                <Text variante="petit" ton="attenue" as="span" className="block truncate">
                    {details}
                </Text>
                {(urgent || vocal || avecStatut) && (
                    <span className="mt-1 flex flex-wrap items-center gap-1.5">
                        {urgent && (
                            <span className="rounded-full bg-alert-text-color px-2 py-0.5">
                                <Text variante="petit" as="span" className="font-semibold text-white">
                                    {t('maintenance.carte.urgent')}
                                </Text>
                            </span>
                        )}
                        {avecStatut && (
                            <span className="rounded-full bg-main-color-light px-2 py-0.5">
                                <Text variante="petit" ton="marque" as="span">
                                    {libelleStatutPanne(panne.status)}
                                </Text>
                            </span>
                        )}
                        {vocal && (
                            <MicrophoneIcon aria-label={t('maintenance.carte.messageVocal')} className="h-5 w-5 text-main-color" />
                        )}
                    </span>
                )}
            </span>
            <ChevronRightIcon aria-hidden="true" className="h-5 w-5 shrink-0 text-text-color-light" />
        </button>
    );
}
